import type { ReactNode } from 'react'

import {
  EXCHANGE_REASON_CODES,
  EXCHANGE_REASON_LABELS,
  type ExchangeReasonCode,
} from '@aeris/contracts'
import { Button } from '@aeris/ui'

/**
 * Por que a peça voltou — F158.
 *
 * ## Por que é um componente, e não três blocos parecidos
 *
 * A pergunta aparece em **três** telas: o pedido do caixa
 * (`exchange-request-dialog`), a troca do trocador (`new-exchange-dialog`) e a
 * troca de produto de fora (`outside-exchange-dialog`). Elas não se parecem em
 * mais nada, mas nesta pergunta têm de ser idênticas — se a ordem dos botões
 * ou o rótulo mudar entre elas, quem opera as duas escolhe por posição e acerta
 * a errada.
 *
 * ## Os botões, e não um `select`
 *
 * São cinco opções curtas numa tela que se usa com pressa e às vezes no
 * toque. O `select` custa dois gestos (abrir, escolher) e esconde as opções até
 * o primeiro; cinco botões custam um e ficam à vista. É a mesma forma que a
 * tela do caixa já usava para os "motivos comuns" da F136 — o balcão não
 * precisa aprender nada.
 *
 * `aria-pressed` e não `role="radio"`: são botões de alternância dentro de um
 * grupo rotulado, que é o que o leitor de tela anuncia corretamente sem que
 * este componente precise implementar a navegação por setas de um radiogroup.
 */

export type ExchangeReasonPickerProps = {
  value: ExchangeReasonCode | null
  onValueChange: (code: ExchangeReasonCode) => void
  disabled?: boolean
  /**
   * O que este grupo está perguntando (F178).
   *
   * Na nota com duas peças, a pergunta aparece uma vez por peça além da da
   * troca — e três grupos com o mesmo nome fariam o leitor de tela (e o teste)
   * escolherem por posição.
   */
  label?: string
}

export const ExchangeReasonPicker = ({
  value,
  onValueChange,
  disabled = false,
  label = 'Por que a peça voltou',
}: ExchangeReasonPickerProps): ReactNode => (
  <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
    {EXCHANGE_REASON_CODES.map((code) => (
      <Button
        key={code}
        type="button"
        size="sm"
        disabled={disabled}
        variant={value === code ? 'primary' : 'secondary'}
        aria-pressed={value === code}
        onClick={() => {
          onValueChange(code)
        }}
      >
        {EXCHANGE_REASON_LABELS[code]}
      </Button>
    ))}
  </div>
)
